/* =============================================================================
   VIEWS / ADMIN / PURCHASE-ORDERS-ADMIN — Bestellwesen (Phase E4b, S163)
   =============================================================================
   Subtab „Bestellungen" der Gruppe „Ware". Bestellvorschlag (unter Mindestbestand,
   je Hauptlieferant) · Bestellungen anlegen (Lieferant → Positionen) · absenden ·
   Wareneingang gegen Bestellung (bucht über den E1-Ledger, source='order'). Alles über
   Manager-gated SECURITY-DEFINER-RPCs (Migration phase_122); Server gated hart.

   Non-destruktive Renders; Detail im Modal; S161-Live-View-Registry für Refresh.
   Inline-Handler ohne window.func() (CSP). Hinter Flag `ingredientsDb`.
============================================================================ */

import { htmlEscape, showToast } from '../../utils/helpers.js';
import { formatEuro } from '../../core/food-cost.js';
import { formatStock } from '../../core/stock.js';
import { poStatusLabel, poStatusColor, reorderUnits, outstandingQty } from '../../core/purchase-orders.js';
import { registerLiveView } from '../../core/live-view-registry.js';

let _orders = [];          // purchase_orders_overview()
let _suggestions = [];     // suggest_reorder()
let _detail = null;        // { po, items } aktuell offene Bestellung
let _ingredients = [];     // Zutaten-Cache (für Positions-Anlage)

function flagOn() {
  return !!(window.AppConfig && window.AppConfig.features && window.AppConfig.features.ingredientsDb);
}
function storage() { return window.App && window.App.storage; }

let _poLiveRegistered = false;
function ensurePoLiveRegistered() {
  if (_poLiveRegistered) return;
  _poLiveRegistered = true;
  registerLiveView({ tabId: 'tab-purchase-orders', render: renderPurchaseOrdersAdmin, events: ['order:realtime'] });
}

// -----------------------------------------------------------------------------
// Tab-Render
// -----------------------------------------------------------------------------
export async function renderPurchaseOrdersAdmin() {
  ensurePoLiveRegistered();
  const el = document.getElementById('tab-purchase-orders');
  if (!el) return;
  if (!flagOn()) {
    el.innerHTML = `<div class="card"><p class="hint">Bestellungen sind deaktiviert (Feature-Flag <code>ingredientsDb</code>).</p></div>`;
    return;
  }
  el.innerHTML = `<div class="card"><p class="hint">Lade Bestellungen…</p></div>`;
  try {
    [_orders, _suggestions] = await Promise.all([
      storage().purchaseOrders.overview(),
      storage().purchaseOrders.suggestReorder()
    ]);
  } catch (e) {
    console.warn('[po] load:', e);
    _orders = []; _suggestions = [];
  }
  el.innerHTML = renderSuggestionHtml() + renderListHtml();
}

// -----------------------------------------------------------------------------
// Bestellvorschlag (unter Mindestbestand, je Hauptlieferant)
// -----------------------------------------------------------------------------
function renderSuggestionHtml() {
  const valid = _suggestions.filter(s => s.supplierId && s.suggestedUnits > 0);
  if (!valid.length) return '';
  // Gruppieren je Hauptlieferant
  const bySupplier = {};
  for (const s of valid) {
    (bySupplier[s.supplierId] = bySupplier[s.supplierId] || { name: s.supplierName, items: [] }).items.push(s);
  }
  const groups = Object.entries(bySupplier).map(([sid, g]) => {
    const rows = g.items.map(s => `<li>${htmlEscape(s.ingredientName)} — <strong>${htmlEscape(String(s.suggestedUnits))}× ${htmlEscape(s.purchaseUnit || 'Einheit')}</strong> <span class="hint" style="font-size:.78rem;">(Bestand ${formatStock(s.stockBase, s.baseUnit)} / Soll ${formatStock(s.minStockBase, s.baseUnit)})</span></li>`).join('');
    return `
      <div style="margin-top:8px; padding:10px 12px; border:1px solid var(--border,#e2e2e2); border-radius:8px; background:var(--bg-elev);">
        <div style="display:flex; justify-content:space-between; align-items:center; gap:10px; flex-wrap:wrap;">
          <strong>${htmlEscape(g.name)}</strong>
          <button class="btn btn-secondary btn-sm" onclick="poApplySuggestion('${htmlEscape(sid)}')">→ Bestellung anlegen (${g.items.length})</button>
        </div>
        <ul style="margin:6px 0 0; padding-left:18px; font-size:.86rem;">${rows}</ul>
      </div>`;
  }).join('');
  const noSupplier = _suggestions.filter(s => !s.supplierId).map(s => htmlEscape(s.ingredientName));
  const noSupHint = noSupplier.length
    ? `<p class="hint" style="font-size:.8rem; margin:8px 0 0; color:var(--danger,#b94a48);">Ohne Hauptlieferant (nicht vorschlagbar): ${noSupplier.join(' · ')}</p>` : '';
  return `
    <div class="card" style="margin-bottom:14px; background:var(--warn-soft,#fff4e0); border:1px solid var(--warn,#d9a227);">
      <h2 style="margin:0; font-size:1.05rem;">⚠️ Bestellvorschlag — ${valid.length} Position(en) unter Mindestbestand</h2>
      <p class="hint" style="margin:4px 0 0;">Vorschlagsmenge je Hauptlieferant, auf ganze Einkaufseinheiten aufgerundet.</p>
      ${groups}
      ${noSupHint}
    </div>`;
}

// -----------------------------------------------------------------------------
// Bestellungs-Liste
// -----------------------------------------------------------------------------
function renderListHtml() {
  const rows = _orders.map(renderRowHtml).join('');
  return `
    <div class="card">
      <div style="display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap;">
        <div>
          <h2 style="margin:0;">Bestellungen</h2>
          <p class="hint" style="margin:4px 0 0;">Wareneingang gegen Bestellung bucht über das Lager (Single-Source). ${_orders.length} Bestellung(en).</p>
        </div>
        <button class="btn btn-primary btn-sm" onclick="poOpenCreate()">+ Bestellung</button>
      </div>
      <div style="margin-top:14px; display:flex; flex-direction:column; gap:6px;">
        ${rows || '<div class="hint" style="padding:16px; text-align:center;">Noch keine Bestellungen.</div>'}
      </div>
    </div>`;
}

function renderRowHtml(po) {
  const dot = `<span style="display:inline-block; padding:2px 8px; border-radius:10px; font-size:.74rem; color:#fff; background:${poStatusColor(po.status)};">${poStatusLabel(po.status)}</span>`;
  const pct = po.status === 'partially_received' ? ` <span class="hint" style="font-size:.78rem;">${po.receivedPct}%</span>` : '';
  return `
    <div data-po="${htmlEscape(po.id)}"
         style="display:grid; grid-template-columns:auto 1fr auto auto; gap:12px; align-items:center; padding:10px 14px; background:var(--bg-soft,#f6f6f6); border:1px solid var(--border,#e2e2e2); border-radius:8px;">
      <div style="font-weight:700; font-variant-numeric:tabular-nums;">${htmlEscape(po.orderNumber)}</div>
      <div style="min-width:0;">
        <div style="font-weight:600;">${htmlEscape(po.supplierName || '—')}</div>
        <div class="hint" style="font-size:.8rem;">${po.itemCount} Position(en) · ${formatEuro(po.totalAmount)}${po.expectedAt ? ' · erwartet ' + htmlEscape(po.expectedAt) : ''}</div>
      </div>
      <div>${dot}${pct}</div>
      <button class="btn btn-ghost btn-sm" onclick="poOpenDetail('${htmlEscape(po.id)}')">Öffnen</button>
    </div>`;
}

// -----------------------------------------------------------------------------
// Bestellung anlegen (Lieferant wählen → Draft)
// -----------------------------------------------------------------------------
export async function poOpenCreate() {
  let suppliers = [];
  try { suppliers = (await storage().suppliers.overview()).filter(s => s.active); } catch (_) { suppliers = []; }
  if (!suppliers.length) { showToast('Erst einen Lieferanten anlegen (Tab „Lieferanten")', 'error'); return; }
  const overlay = document.getElementById('modal-overlay');
  const content = document.getElementById('modal-content');
  if (!content || !overlay) return;
  content.innerHTML = `
    <div class="modal-header"><h2>Neue Bestellung</h2><button class="modal-close" onclick="closeModal()">×</button></div>
    <div class="field"><label>Lieferant *</label>
      <select id="po-supplier">${suppliers.map(s => `<option value="${htmlEscape(s.id)}">${htmlEscape(s.name)}</option>`).join('')}</select></div>
    <div class="field-row">
      <div class="field"><label>Erwartet am (optional)</label><input id="po-expected" type="date"></div>
      <div class="field"><label>Notiz (optional)</label><input id="po-note" type="text"></div>
    </div>
    <div class="btn-row" style="margin-top:14px;">
      <button class="btn btn-primary" onclick="poCreateSubmit()">Anlegen → Positionen</button>
      <button class="btn btn-ghost" onclick="closeModal()">Abbrechen</button>
    </div>`;
  overlay.classList.add('active');
}

export async function poCreateSubmit() {
  const sup = document.getElementById('po-supplier');
  const exp = document.getElementById('po-expected');
  const note = document.getElementById('po-note');
  if (!sup || !sup.value) { showToast('Bitte einen Lieferanten wählen', 'error'); return; }
  try {
    const po = await storage().purchaseOrders.create(sup.value, exp && exp.value || null, note && note.value || null);
    showToast('Bestellung angelegt', 'success');
    await renderPurchaseOrdersAdmin();
    if (po) await poOpenDetail(po.id);
  } catch (e) {
    console.error('[po] create:', e);
    showToast('Anlegen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// Bestellvorschlag → Bestellung je Hauptlieferant anlegen + Positionen übernehmen
export async function poApplySuggestion(supplierId) {
  const items = _suggestions.filter(s => s.supplierId === supplierId && s.suggestedUnits > 0);
  if (!items.length) return;
  try {
    const po = await storage().purchaseOrders.create(supplierId, null, 'Aus Bestellvorschlag');
    for (const s of items) {
      try { await storage().purchaseOrders.addItem(po.id, s.ingredientId, s.ingredientSupplierId, s.suggestedUnits); }
      catch (e) { console.warn('[po] addItem (suggestion):', e); }
    }
    showToast(`Bestellung mit ${items.length} Position(en) angelegt`, 'success');
    await renderPurchaseOrdersAdmin();
    await poOpenDetail(po.id);
  } catch (e) {
    console.error('[po] applySuggestion:', e);
    showToast('Übernehmen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// -----------------------------------------------------------------------------
// Detail-Modal (Status-abhängig: Entwurf = Positionen pflegen; sonst Wareneingang)
// -----------------------------------------------------------------------------
export async function poOpenDetail(poId) {
  const po = _orders.find(o => o.id === poId) || (await storage().purchaseOrders.overview()).find(o => o.id === poId);
  if (!po) { showToast('Bestellung nicht gefunden', 'error'); return; }
  let items = [];
  try { items = await storage().purchaseOrders.detail(poId); } catch (_) { items = []; }
  _detail = { po, items };
  const overlay = document.getElementById('modal-overlay');
  const content = document.getElementById('modal-content');
  if (!content || !overlay) return;
  content.innerHTML = renderDetailHtml();
  overlay.classList.add('active');
  if (po.status === 'draft' && !_ingredients.length) {
    try { _ingredients = await storage().ingredients.list(); } catch (_) { _ingredients = []; }
    renderAddItemRow();
  }
}

function renderDetailHtml() {
  const { po, items } = _detail;
  const isDraft = po.status === 'draft';
  const canReceive = po.status === 'ordered' || po.status === 'partially_received';
  const rows = items.map(it => renderItemRowHtml(it, isDraft, canReceive)).join('');
  const actions = isDraft
    ? `<button class="btn btn-primary" onclick="poSubmit('${htmlEscape(po.id)}')">Absenden (bestellen)</button>
       <button class="btn btn-ghost" style="color:var(--danger,#b94a48);" onclick="poCancel('${htmlEscape(po.id)}')">Verwerfen</button>`
    : (po.status !== 'received' && po.status !== 'cancelled'
        ? `<button class="btn btn-ghost" style="color:var(--danger,#b94a48);" onclick="poCancel('${htmlEscape(po.id)}')">Stornieren</button>` : '');
  return `
    <div class="modal-header">
      <h2>${htmlEscape(po.orderNumber)} · ${htmlEscape(po.supplierName || '')}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <p class="hint" style="margin:0 0 8px;">Status: <span style="padding:2px 8px; border-radius:10px; font-size:.74rem; color:#fff; background:${poStatusColor(po.status)};">${poStatusLabel(po.status)}</span>${po.note ? ' · ' + htmlEscape(po.note) : ''}</p>
    <div style="max-height:46vh; overflow:auto;">
      <table style="width:100%; border-collapse:collapse; font-size:.88rem;">
        <thead><tr style="text-align:left; color:var(--ink-soft,#666);">
          <th style="padding:5px 6px;">Zutat</th>
          <th style="padding:5px 6px; text-align:right;">Bestellt</th>
          <th style="padding:5px 6px; text-align:right;">Erhalten</th>
          <th style="padding:5px 6px;"></th>
        </tr></thead>
        <tbody>${rows || '<tr><td colspan="4" class="hint" style="padding:12px; text-align:center;">Noch keine Positionen.</td></tr>'}</tbody>
      </table>
    </div>
    ${isDraft ? `<div id="po-additem" style="margin-top:10px;"></div>` : ''}
    <div class="btn-row" style="margin-top:14px;">${actions}<button class="btn btn-ghost" onclick="closeModal()">Schließen</button></div>`;
}

function renderItemRowHtml(it, isDraft, canReceive) {
  const unit = it.purchaseUnit || 'Einheit';
  const ordered = `${htmlEscape(String(it.qtyOrdered))} ${htmlEscape(unit)}${it.price != null ? ` <span class="hint" style="font-size:.74rem;">(${formatEuro(it.price)}/${htmlEscape(unit)})</span>` : ''}`;
  const received = `${htmlEscape(String(it.qtyReceived || 0))} ${htmlEscape(unit)}`;
  let action = '';
  if (isDraft) {
    action = `<button class="btn btn-ghost btn-xs" style="color:var(--danger,#b94a48);" onclick="poRemoveItem('${htmlEscape(it.itemId)}')">entfernen</button>`;
  } else if (canReceive) {
    const rest = outstandingQty(it.qtyOrdered, it.qtyReceived);
    // E6a — optionales MHD beim Wareneingang gegen Bestellung → Charge.
    action = `<input id="rcv-${htmlEscape(it.itemId)}" type="number" step="0.01" min="0" value="${rest || ''}" placeholder="${rest}" style="width:64px; text-align:right;" title="Menge (Einkaufseinheit)">
              <input id="rcvmhd-${htmlEscape(it.itemId)}" type="date" style="width:140px;" title="MHD (optional) — erzeugt eine Charge">
              <button class="btn btn-secondary btn-xs" onclick="poReceiveItem('${htmlEscape(it.itemId)}')">empfangen</button>`;
  }
  return `
    <tr style="border-top:1px solid var(--border,#eee);">
      <td style="padding:5px 6px; font-weight:600;">${htmlEscape(it.ingredientName)}</td>
      <td style="padding:5px 6px; text-align:right; white-space:nowrap;">${ordered}</td>
      <td style="padding:5px 6px; text-align:right; white-space:nowrap;">${received}</td>
      <td style="padding:5px 6px; text-align:right; white-space:nowrap;">${action}</td>
    </tr>`;
}

// Positions-Anlage (nur Entwurf): Zutat → deren Lieferanten-Verknüpfung für diesen Lieferanten
function renderAddItemRow() {
  const wrap = document.getElementById('po-additem');
  if (!wrap || !_detail) return;
  const opts = _ingredients.map(i => `<option value="${htmlEscape(i.id)}">${htmlEscape(i.name)}</option>`).join('');
  wrap.innerHTML = `
    <div style="padding:10px; border:1px dashed var(--border,#ccc); border-radius:8px;">
      <div class="field-row" style="align-items:flex-end; flex-wrap:wrap;">
        <div class="field" style="min-width:160px;"><label style="font-size:.8rem;">Zutat</label><select id="po-add-ing">${opts}</select></div>
        <div class="field" style="max-width:120px;"><label style="font-size:.8rem;">Menge (Einkaufseinheit)</label><input id="po-add-qty" type="number" step="0.01" min="0" placeholder="1"></div>
        <button class="btn btn-secondary btn-sm" onclick="poAddItem()">+ Position</button>
      </div>
      <p class="hint" style="font-size:.78rem; margin:6px 0 0;">Preis/Einheit kommt aus der Lieferanten-Verknüpfung der Zutat (Tab „Zutaten"). Zutaten ohne Eintrag bei diesem Lieferanten lassen sich nicht hinzufügen.</p>
    </div>`;
}

export async function poAddItem() {
  if (!_detail) return;
  const ingEl = document.getElementById('po-add-ing');
  const qtyEl = document.getElementById('po-add-qty');
  const ingredientId = ingEl && ingEl.value;
  const qty = Number(qtyEl && qtyEl.value);
  if (!ingredientId) { showToast('Bitte eine Zutat wählen', 'error'); return; }
  if (!isFinite(qty) || qty <= 0) { showToast('Menge muss > 0 sein', 'error'); return; }
  // Lieferanten-Verknüpfung der Zutat für DIESEN Lieferanten finden (Snapshot-Quelle)
  let link = null;
  try {
    const links = await storage().ingredientSuppliers.listFor(ingredientId);
    link = links.find(l => l.supplierId === _detail.po.supplierId) || null;
  } catch (_) { link = null; }
  if (!link) { showToast('Diese Zutat hat keinen Eintrag bei diesem Lieferanten (im Zutaten-Editor anlegen)', 'error'); return; }
  try {
    await storage().purchaseOrders.addItem(_detail.po.id, ingredientId, link.id, qty);
    await poOpenDetail(_detail.po.id); // Detail neu laden (non-destruktiv: Modal-Inhalt)
    await refreshListSilently();
  } catch (e) {
    console.error('[po] addItem:', e);
    showToast('Hinzufügen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function poRemoveItem(itemId) {
  if (!_detail) return;
  try {
    await storage().purchaseOrders.removeItem(itemId);
    await poOpenDetail(_detail.po.id);
    await refreshListSilently();
  } catch (e) {
    console.error('[po] removeItem:', e);
    showToast('Entfernen fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function poSubmit(poId) {
  try {
    await storage().purchaseOrders.submit(poId);
    showToast('Bestellung abgesendet', 'success');
    await renderPurchaseOrdersAdmin();
    await poOpenDetail(poId);
  } catch (e) {
    console.error('[po] submit:', e);
    showToast('Absenden fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function poCancel(poId) {
  if (!(await window.styledConfirm({ title: 'Bestellung stornieren/verwerfen?', danger: true, confirmLabel: 'Verwerfen' })).confirmed) return;
  try {
    await storage().purchaseOrders.cancel(poId);
    showToast('Bestellung storniert', 'success');
    if (typeof window.closeModal === 'function') window.closeModal();
    await renderPurchaseOrdersAdmin();
  } catch (e) {
    console.error('[po] cancel:', e);
    showToast('Stornieren fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

export async function poReceiveItem(itemId) {
  if (!_detail) return;
  const inp = document.getElementById('rcv-' + itemId);
  const mhdInp = document.getElementById('rcvmhd-' + itemId);
  const qty = Number(inp && inp.value);
  const mhd = (mhdInp && mhdInp.value) || null;
  if (!isFinite(qty) || qty <= 0) { showToast('Empfangsmenge muss > 0 sein', 'error'); return; }
  try {
    await storage().purchaseOrders.receiveItem(itemId, qty, mhd);
    showToast('Wareneingang gebucht — Lager aktualisiert', 'success');
    await renderPurchaseOrdersAdmin();        // Liste + Status frisch
    await poOpenDetail(_detail.po.id);         // Detail frisch (qty_received/Status)
  } catch (e) {
    console.error('[po] receive:', e);
    showToast('Wareneingang fehlgeschlagen: ' + (e.message || e), 'error');
  }
}

// Liste im Hintergrund aktualisieren, ohne das offene Modal zu schließen.
async function refreshListSilently() {
  try {
    [_orders, _suggestions] = await Promise.all([
      storage().purchaseOrders.overview(), storage().purchaseOrders.suggestReorder()
    ]);
    const el = document.getElementById('tab-purchase-orders');
    if (el) el.innerHTML = renderSuggestionHtml() + renderListHtml();
  } catch (_) { /* tolerant */ }
}

if (typeof window !== 'undefined') {
  Object.assign(window, {
    renderPurchaseOrdersAdmin, poOpenCreate, poCreateSubmit, poApplySuggestion,
    poOpenDetail, poAddItem, poRemoveItem, poSubmit, poCancel, poReceiveItem
  });
}
